import Vue from 'vue';
import VirtualList from 'vue-virtual-scroll-list';
import { mapGetters } from 'vuex';
import FloatingDropdownItem from './FloatingDropdownItem.vue';
import FloatingDropdownSuggestion from './FloatingDropdownSuggestion.vue';
import { BfSpinner } from "components/Butterfly";
import DataUtils from 'src/utils/data';
import escapeRegExp from 'escape-string-regexp';
import { debounce } from 'lodash';
export default Vue.extend({
    components: { VirtualList, FloatingDropdownSuggestion, BfSpinner },
    // required for old vee-validate2
    $_veeValidate: { validator: 'new' },
    props: {
        'searchable': { type: Boolean, default: false },
        'allowAny': { type: Boolean, default: false },
        'infoMsg': { type: String, default: undefined },
        /** A map of option value => colour for display purposes */
        'circleColours': { type: Object, default: () => ({}) },
        'removable': { type: Boolean, default: false },
        'loading': { type: Boolean, default: false },
        'options': { type: Array, required: true },
        'suggestions': { type: Array, required: false, default: undefined },
        'triggerSel': { type: String, default: undefined },
        'value': { type: [String, Number], default: '' },
        'searchPlaceholder': { type: String, default: "Type anything..." },
        'validationRules': { type: String, default: '' },
        'header': { type: String, required: false, default: '' },
        'phraseSynonyms': { type: Boolean, default: false },
        'wordMode': { type: String, default: 'single_and_multi_word' },
        'initialValue': { type: String, default: '' },
    },
    data() {
        return {
            activatedIndex: -1,
            highlightedIndex: -1,
            itemComponent: FloatingDropdownItem,
            searchValue: this.initialValue,
            leftOffset: 0,
            resize: debounce(this.preventScreenOverflow.bind(this), 100),
            maxHeight: 0,
        };
    },
    computed: {
        ...mapGetters([
            'currentModel'
        ]),
        filteredOptions() {
            if (this.optionFilterRegex) {
                return this.frozenOptions.filter(o => o.match(this.optionFilterRegex));
            }
            return this.frozenOptions;
        },
        frozenOptions() {
            return DataUtils.deepFreeze(this.options);
        },
        hasVisibleOptions() {
            return this.frozenOptions && this.frozenOptions.find(o => o.match(this.optionFilterRegex)) !== undefined;
        },
        batchSelections() {
            return this.suggestions.filter(s => s.toggled === true);
        },
        optionFilterRegex() {
            return new RegExp(escapeRegExp(this.searchValue), 'i');
        },
        scrollerItems() {
            const items = this.filteredOptions.map((o, i) => {
                const d = {
                    id: i,
                    value: o,
                    isTheme: this.header === 'Themes',
                    clickHandler: (v) => this.activateValue(v)
                };
                return d;
            });
            if (this.removable) {
                items.splice(0, 0, {
                    id: -1,
                    value: '(Remove from line)',
                    clickHandler: () => this.removeSelf()
                });
            }
            return items;
        },
        searchPromptText() {
            const v = this.searchValue.replace(/^\"|\"$/g, '');
            if (v.length === 0) {
                return '';
            }
            else if (this.searchValue.startsWith('"')) {
                return `"${v}"`;
            }
            else {
                return `${v}`;
            }
        },
        searchPromptVisible() {
            if (!this.allowAny && this.hasVisibleOptions) {
                // Search prompt only shown when free input is allowed
                // or when there are no options available
                return false;
            }
            const v = this.searchValue.replace(/^\"|\"$/g, '').trim(); // remove quotes
            if (this.searchable && v.length > 0) {
                return true;
            }
            return false;
        },
        validationError() { return this.errors.first('inputText'); },
        suggestionsMode() {
            return this.suggestions && !this.searchValue;
        }
    },
    mounted() {
        const dictionary = {
            custom: {
                inputText: {
                    regex: 'Must only include numbers (0-9)',
                    max_value: 'Number limit exceeded',
                }
            }
        };
        this.$validator.localize('en', dictionary);
        // Bind toggle handler to trigger element
        this.$nextTick(() => {
            let triggerEl = this.$el.parentNode;
            if (this.triggerSel) {
                triggerEl = triggerEl.querySelector(this.triggerSel);
            }
            triggerEl.addEventListener('click', this.onParentClick.bind(this));
            // Setup click handler on document in order to hide dropdown
            document.addEventListener('click', this.onDocumentClick.bind(this));
            // Setup keydown handler on document
            document.addEventListener('keydown', this.onKeydown.bind(this));
        });
        window.addEventListener('resize', this.resize);
    },
    beforeDestroy: function () {
        // Cleanup document handlers
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.resize);
    },
    methods: {
        // Activate the specifed value and hide/reset the dropdown
        activateValue(value, modifierKeyPressed) {
            this.$emit('value-selected', { value: value, modifierKeyPressed: modifierKeyPressed });
            // Hide & reset dropdown
            this.hide();
        },
        // Select a synonym suggestion value and fire off event
        selectSynonymSuggestion(synonym, rank) {
            this.activateValue(synonym.name);
            this.$emit('synonym-selected', synonym, rank);
        },
        // Toggle interim selection of a synonym suggestion, without closing the dropdown
        toggleSynonymSelection(synonym) {
            let suggestion = this.suggestions.find(s => s.name === synonym.name);
            Vue.set(suggestion, 'toggled', !suggestion.toggled);
        },
        // Add the batch selections to the current query, closing the dropdown
        addBatchSelections() {
            this.hide();
            this.$emit('batch-synonym-selection', this.batchSelections);
        },
        resetBatchSelections() {
            this.suggestions.forEach(s => s.toggled = false);
        },
        // Get all visible dropdown items
        getVisibleItems() {
            let nodes = this.$el.querySelectorAll('.item:not(.hidden)');
            return Array.from(nodes);
        },
        // Hide and reset the dropdown
        hide() {
            this.$el?.classList?.remove('visible');
            this.leftOffset = 0;
            this.highlightReset();
            this.errors.clear();
            if (this.searchable) {
                // Reset search text
                this.searchValue = '';
            }
        },
        // Highlight the next visible item
        highlightNextItem() {
            let visibleItems = this.getVisibleItems();
            if (visibleItems.length === 0) {
                // No items to search - don't do anything
                return;
            }
            for (let itemEl of visibleItems) {
                let index = parseInt(itemEl.dataset.index, 10);
                if (index > this.highlightedIndex) {
                    this.highlightReset();
                    itemEl.classList.add('highlight');
                    this.highlightedIndex = index;
                    this.highlightScroll();
                    return;
                }
            }
        },
        // Highlight the previous visible item
        highlightPrevItem() {
            let visibleItems = this.getVisibleItems();
            if (visibleItems.length === 0) {
                // No items to search - don't do anything
                return;
            }
            for (let itemEl of visibleItems.reverse()) {
                let index = parseInt(itemEl.dataset.index, 10);
                if (index < this.highlightedIndex) {
                    this.highlightReset();
                    itemEl.classList.add('highlight');
                    this.highlightedIndex = index;
                    this.highlightScroll();
                    return;
                }
            }
            // If we got here, it means we are at the start of items with nowhere to go;
            // so we move focus back to input (if the dropdown is searchable)
            if (this.searchable) {
                this.$refs.searchInput.focus();
                this.highlightReset();
            }
        },
        // Ensure the dropdown menu scroll allows the highlighted item to be seen.
        highlightScroll(edgeTolerance = 5) {
            let item = this.$el.querySelector('.item.highlight');
            let menu = this.$el.querySelector('.menu');
            let itemOffset = item.offsetTop;
            let itemHeight = item.offsetHeight;
            let menuScroll = menu.scrollTop;
            let menuOffset = menu.offsetTop;
            let menuHeight = menu.offsetHeight;
            if (menuScroll + menuHeight < (itemOffset + itemHeight + edgeTolerance)) {
                // Item is below viewable area
                menu.scrollTop = (itemOffset + itemHeight + edgeTolerance) - menuHeight;
            }
            if (itemOffset - menuOffset < menuScroll) {
                // Item is above viewable area
                menu.scrollTop = itemOffset - menuOffset;
            }
        },
        // Reset any highlighting
        highlightReset() {
            this.highlightedIndex = -1;
            let highlightedItem = this.$el.querySelector('.item.highlight');
            if (highlightedItem) {
                highlightedItem.classList.remove('highlight');
            }
        },
        // Is this dropdown visible?
        isVisible() {
            return this.$el.classList.contains('visible');
        },
        // Handles when this dropdown was clicked.
        // Set `internalClick` flag to avoid unwanted hiding of the component.
        onClick() {
            this.internalClick = true;
        },
        // Handle enter on menu item
        onEnterMenuItem(modifierKeyPressed) {
            let item = this.$el?.querySelector(`.item[data-index="${this.highlightedIndex}"]`);
            this.activateValue(item.dataset.value, modifierKeyPressed);
            // Keep higlight at activated item for next show of the dropdown
            this.highlightedIndex = item.dataset.index;
        },
        // Handles keydown in this dropdown.
        // Concerned with navigation and selection of menu items.
        onKeydown(event) {
            if (!this.isVisible()) {
                // Ignore keydown events if dropdown is not visible
                return;
            }
            if (event.key === 'Escape') {
                // Hide dropdown on escape key
                this.hide();
            }
            else if (event.key === 'ArrowDown') {
                this.highlightNextItem();
                event.preventDefault(); // Prevent moving cursor in search input field
            }
            else if (event.key === 'ArrowUp') {
                this.highlightPrevItem();
                event.preventDefault(); // Prevent moving cursor in search input field
            }
            else if (!this.searchable && event.key === 'Enter') {
                // Handle enter keydown if not searchable
                // (otherwise this is handled by search input)
                this.onEnterMenuItem();
            }
        },
        // Handles when parent element of this dropdown component is clicked.
        // Used to toggle visibility.
        onParentClick(event) {
            this.internalClick = true;
            // Don't do anything if the click came from within the dropdown itself
            if (this.$el.contains(event.srcElement)) {
                return;
            }
            // Otherwise toggle visibility
            if (this.$el.classList.contains('visible')) {
                this.hide();
            }
            else {
                this.show();
            }
        },
        // Handles when the document is clicked.
        // Used to hide this dropdown. (Skip if `internalClick` flag is set)
        onDocumentClick() {
            if (this.internalClick) {
                this.internalClick = false;
                return;
            }
            this.hide();
        },
        // Triggered whenever the search text for a value dropdown changes.
        // We use this to trigger updating of the dropdown's search prompt.
        onValueSearch() {
            this.highlightReset();
        },
        // Handle free text search by enter keydown
        onValueSearchEnter(event) {
            // The control key is a modifier to support alternate
            // behaviours when reacting to the search value selection.
            const modifierKeyPressed = event.ctrlKey;
            if (this.validationError) {
                return;
            }
            if (this.highlightedIndex >= 0) {
                // If we have higlighted index, we are in item navigation and should activate that
                this.onEnterMenuItem(modifierKeyPressed);
            }
            else {
                // Otherwise, activate search value if searching is allowed,
                // or if it matches an available option
                if (this.searchValue.length > 0 && this.searchable && (this.options && this.options.indexOf(this.searchValue) >= 0 || this.allowAny)) {
                    this.activateValue(this.searchValue, modifierKeyPressed);
                }
            }
        },
        // Remove this dropdown value entirely
        removeSelf() {
            this.$emit('remove');
        },
        // Prevent dropdown from overflowing the screen
        preventScreenOverflow() {
            if (!this.isVisible())
                return;
            const rect = this.$el.getBoundingClientRect();
            const overflow = Math.max(0, (rect.left + rect.width - this.leftOffset) - window.innerWidth);
            this.leftOffset = -overflow;
            const win = window.document.documentElement.getBoundingClientRect();
            const verticalOverflow = rect.bottom - win.bottom;
            this.maxHeight = rect.height - verticalOverflow - 10; //leave 10px space between menu & bottom of screen
        },
        // Show the dropdown
        show() {
            this.$el.classList.add('visible');
            this.preventScreenOverflow();
            if (this.searchable) {
                this.searchValue = this.initialValue;
                this.$refs.searchInput.focus();
                this.$nextTick(() => {
                    this.$refs.searchInput.select();
                });
            }
        },
        updateWordMode(value) {
            this.$emit('update:word-mode', value);
        },
    }
});
